/**
 * @file: KpiCard.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * One KPI tile in the lead-list summary grid: a big value over its label on the
 * left, a soft-tinted icon chip on the right. Tone (icon color + chip bg) is
 * passed in so each metric can carry its own accent.
 */

import { Typography } from '@euler/app/components/typography';
import {
  LD,
  cardShadowStyle,
} from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LucideIcon } from 'lucide-react-native';
import { memo } from 'react';
import { View } from 'react-native';

export const KpiCard = memo(
  ({
    value,
    label,
    icon: Icon,
    iconColor,
    iconBg,
  }: {
    value: number | undefined;
    label: string;
    icon: LucideIcon;
    iconColor: string;
    iconBg: string;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: rpx(62),
          paddingHorizontal: rpx(12),
          borderRadius: rpx(14),
          backgroundColor: LD.surface,
          ...cardShadowStyle(rpx),
        }}
      >
        <View style={{ gap: rpx(2) }}>
          <Typography.Text size={rpx(20)} color={LD.ink} bold>
            {value ?? '—'}
          </Typography.Text>
          <Typography.Text size={rpx(11)} color={LD.mist} bold>
            {label}
          </Typography.Text>
        </View>
        <View
          style={{
            width: rpx(28),
            height: rpx(28),
            borderRadius: rpx(9),
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: iconBg,
          }}
        >
          <Icon size={rpx(15)} color={iconColor} />
        </View>
      </View>
    );
  },
);
